import { useState, useRef } from "react";
import ToolShell from "./ToolShell";
import ToolExportButton from "./ToolExportButton";
import { estimateNightly, estimateOccupancy, monthlyGross, netMonthly, fmt } from "./calculatorData";

const inputStyle = { border: "1.5px solid #E5E5E5", borderRadius: 10, padding: "13px 15px", fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#1A1A1A", background: "#fff", width: "100%", boxSizing: "border-box", outline: "none", transition: "border-color 0.2s" };
const labelStyle = { fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: "#717171", display: "block", marginBottom: 7 };
const cardStyle = { background: "#fff", border: "1px solid #EBEBEB", borderRadius: 18, padding: 28, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" };

function Metric({ label, value, accent }) {
  return (
    <div style={{ background: "#F7F7F7", borderRadius: 14, padding: "18px 20px", border: "1px solid #F0F0F0" }}>
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#717171", margin: "0 0 7px" }}>{label}</p>
      <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 26, color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{value}</p>
    </div>
  );
}

export default function ShortLetYieldCalculator() {
  const [value, setValue] = useState(250000);
  const [city, setCity] = useState("Manchester");
  const [beds, setBeds] = useState("2 beds");
  const [type, setType] = useState("Apartment");
  const reportRef = useRef(null);

  const nightly = estimateNightly(city, beds, type);
  const occupancy = estimateOccupancy(city);
  const gross = monthlyGross(nightly, occupancy);
  const annualGross = gross * 12;
  const annualNet = netMonthly(gross) * 12;
  const grossYield = value > 0 ? (annualGross / value) * 100 : 0;
  const netYield = value > 0 ? (annualNet / value) * 100 : 0;

  const summary = `Short-let Yield — City: ${city}, ${beds} ${type}. Property value: ${fmt(value)}. Annual gross: ${fmt(annualGross)}. Annual net: ${fmt(annualNet)}. Gross yield: ${grossYield.toFixed(1)}%. Net yield: ${netYield.toFixed(1)}%.`;

  return (
    <ToolShell
      title="Short-let Yield Calculator"
      tagline="Work out the gross and net yield of a property run as a short-let — so you can compare it against any other investment."
      description="Free short-let yield calculator. Calculate gross and net yield for an Airbnb or serviced accommodation property by value, city and bedrooms. Request a personalised report."
      slug="short-let-yield-calculator"
    >
      <div className="calculator-grid" style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 40, alignItems: "start" }}>
        <div style={cardStyle}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#1A1A1A", marginTop: 0, marginBottom: 20 }}>Investment details</p>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Property value</label>
            <input style={inputStyle} type="number" min="0" value={value} onChange={e => setValue(Math.max(0, Number(e.target.value) || 0))} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>City</label>
            <input style={inputStyle} type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="e.g. Manchester" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Bedrooms</label>
            <select style={inputStyle} value={beds} onChange={e => setBeds(e.target.value)}>
              {["Studio", "1 bed", "2 beds", "3 beds", "4+ beds"].map(b => <option key={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Property type</label>
            <select style={inputStyle} value={type} onChange={e => setType(e.target.value)}>
              {["Apartment", "House", "Cottage", "Other"].map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <ToolExportButton slug="short-let-yield-calculator" title="Short-let Yield" targetRef={reportRef} fileName={`Short-let-Yield-${city}`} />
          <div ref={reportRef} style={{ background: "#fff", padding: 24, borderRadius: 16, border: "1px solid #EBEBEB" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0 16px", borderBottom: "1px solid #F0F0F0", marginBottom: 18 }}>
              <div>
                <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#1A1A1A", margin: 0 }}>Short-let Yield — {city}</p>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#888", margin: "4px 0 0" }}>{beds} · {type}</p>
              </div>
              <img src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/24682ceeb_TruestaysLogo.png" alt="Truestays" style={{ height: 30, width: "auto" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
              <Metric label="Annual gross income" value={fmt(annualGross)} />
              <Metric label="Annual net income" value={fmt(annualNet)} />
            </div>
            <div style={{ ...cardStyle, marginBottom: 0, background: "#FAFAFA" }}>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 16, color: "#1A1A1A", marginTop: 0, marginBottom: 18 }}>Your yield comparison</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: "#F7F7F7", borderRadius: 14, padding: "22px", textAlign: "center", border: "1px solid #E5E5E5" }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#717171", margin: "0 0 7px" }}>Gross yield</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 36, color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{grossYield.toFixed(1)}%</p>
                </div>
                <div style={{ background: "#F0F9F4", borderRadius: 14, padding: "22px", textAlign: "center", border: "1px solid #E0F0E8" }}>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "#1B8B5E", margin: "0 0 7px" }}>Net yield</p>
                  <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 36, color: "#1A1A1A", margin: 0, letterSpacing: "-0.02em" }}>{netYield.toFixed(1)}%</p>
                </div>
              </div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#717171", marginTop: 18, marginBottom: 0, lineHeight: 1.6 }}>Net yield accounts for our 12% + VAT management fee and blended platform fees (~3%). Does not include mortgage payments, insurance, utilities or void periods — these vary by property and financing.</p>
            </div>
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { .calculator-grid { grid-template-columns: 1fr !important; } }`}</style>
    </ToolShell>
  );
}